import { SpotFolderOutlineOpenIcon } from '@tableau/icons/SpotFolderOutlineOpenIcon';
import { SpotHelpMigrateIcon } from '@tableau/icons/SpotHelpMigrateIcon';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ContentRelayPlanDefinition } from '~src/contentRelay/contentDefinitions/ContentRelayPlanDefinition';
import { ExplorerEmptyState } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { SubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { NewRelayPlanButton } from './NewRelayPlanButton';

const PLANS_TAB: SubspaceName = 'contentRelayPlan';
const IMPORTS_TAB: SubspaceName = 'contentRelayImports';

export interface ContentRelaysSpaceProps {
  readonly urls: SpacesSiteUrls;
  /** Which tab is active, driven by the matched route. */
  readonly activeTab: SubspaceName;
}

/**
 * The dedicated Content Relays section: a tabbed space with a "Relay plans" list tab (the existing
 * content-relay plan grid, served by the surrounding SiteSpaceProvider) and an "Import history" tab
 * (empty-state until the import web methods are exposed). The masthead carries the "New plan" button.
 */
export function ContentRelaysSpace(props: ContentRelaysSpaceProps): JSX.Element {
  const urlForSubspace = function(name?: SubspaceName): string {
    return name === IMPORTS_TAB ? props.urls.contentRelayImportsUrl() : props.urls.allContentRelayPlansUrl();
  };

  const subspaces: ReadonlyArray<Subspace> = [
    explorerSubspace({
      name: PLANS_TAB,
      tabLabel: Messages.contentRelayPlansAllTab(),
      supportedDisplayModes: ContentRelayPlanDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      toolbarVisibility: 'enabled',
      supportFilterPanel: true,
      toolbarContentSpecificControls: { beginning: <NewRelayPlanButton /> },
      emptyStateProps: {
        icon: SpotFolderOutlineOpenIcon,
        primaryMessage: Messages.contentRelayPlansEmptyMessage(),
      },
    }),
    {
      name: IMPORTS_TAB,
      tabLabel: Messages.contentRelayImportsTab(),
      shouldRenderTabContentCount: false,
      renderContent: function() {
        return (
          <ExplorerEmptyState
            // SpotHelpMigrateIcon only ships a size-60 variation and throws for any other size.
            // ExplorerEmptyState otherwise calls icon({size: EMPTY_STATE_ICON_SIZE=80}), which crashed
            // the whole space -- so render it explicitly at its supported size.
            icon={function() { return SpotHelpMigrateIcon({ size: 60 }); }}
            primaryMessage={Messages.contentRelayImportsEmptyMessage()}
          />
        );
      },
    },
  ];

  return (
    <TabbedSpace
      subspaces={subspaces}
      activeSubspaceName={props.activeTab}
      urlForSubspace={urlForSubspace}
      mastheadElement={<SpaceHeaderContent headerText={Messages.contentRelayPlansSpaceLabel()} />}
      testIdPrefix={'content-relays'}
      spaceItemName={Messages.contentRelayPlansSpaceLabel()}
    />
  );
}
